import { FC, MouseEvent, useRef, useState } from 'react';
import styled from 'styled-components';

import { ReactComponent as PauseIcon } from '@/assets/icons/audio-pause.svg';
import { ReactComponent as PlayIcon } from '@/assets/icons/audio-play.svg';
import Text from '@/components/Text';
import { ElevenLabsVoice } from '@/types/resources';
import { hexToRgba } from '@/utils/theme';

const Option = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 4px;
  transition: background-color 0.2s ease-out;

  &:hover {
    background-color: ${({ theme }) => theme.colors.grayscaleLight};
  }
`;

const Name = styled(Text)`
  margin-right: 24px;
  margin-left: 12px;
`;

const Badges = styled.div`
  display: flex;
  gap: 8px;
`;

const Badge = styled(Text)<{ $index: number }>`
  border-radius: 50px;
  padding: 4px 8px;
  display: inline-block;
  background: ${({ theme, $index }) =>
    hexToRgba(theme.colors.purple, 1 - $index * 0.08)};
  text-transform: capitalize;
`;

const Audio = styled.audio`
  display: none;
`;

const AudioButton = styled.div`
  line-height: 0;
`;

interface Props {
  voice: ElevenLabsVoice;
  onChange: (voice: ElevenLabsVoice) => void;
}

const VoiceOption: FC<Props> = ({ voice, onChange }) => {
  const ref = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const onPlayPause = (e: MouseEvent) => {
    e.stopPropagation();

    if (isPlaying) {
      setIsPlaying(false);
      ref.current?.pause();
      return;
    }

    setIsPlaying(true);
    ref.current?.play();
  };

  return (
    <Option type="button" onClick={() => onChange(voice)}>
      <AudioButton onClick={onPlayPause}>
        {isPlaying ? <PauseIcon /> : <PlayIcon />}
      </AudioButton>

      <Audio ref={ref} loop preload="none">
        <source src={voice.preview} type="audio/mpeg" />
      </Audio>
      <Name as="span" $type="captionSemibold">
        {voice.name}
      </Name>
      <Badges>
        {voice.labels.map((label, i) => (
          <Badge
            key={i}
            as="span"
            $color="white"
            $type="smallHeadline"
            $index={i}
          >
            {label}
          </Badge>
        ))}
      </Badges>
    </Option>
  );
};

export default VoiceOption;
